import { Link } from 'react-router-dom'
import { CheckCircle2, ExternalLink, GitBranch, Folder, FileText, ListChecks } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Represents a task created by the chatbot through create_orchestration_task
 */
export interface CreatedTask {
  /** The session ID for the created orchestration task */
  sessionId: string
  /** The goal/objective for the task */
  goal: string
  /** Repository name where the task will be executed */
  repositoryName?: string
  /** Project ID the task belongs to */
  projectId?: string
  /** When the task was created */
  createdAt: string
  /** Optional constraints for the task */
  constraints?: string[]
  /** Optional linked work item ID */
  workItemId?: number
}

export interface TaskCreatedCardProps {
  task: CreatedTask
}

/**
 * Displays a card showing a successfully created orchestration task
 * with a link to open the task in the orchestrate page
 */
export function TaskCreatedCard({ task }: TaskCreatedCardProps) {
  const formattedDate = new Date(task.createdAt).toLocaleString()

  return (
    <article
      role="article"
      data-testid="task-created-card"
      data-status="success"
      className={cn(
        'rounded-lg border bg-card text-card-foreground shadow-sm',
        'border-green-200 dark:border-green-800',
        'bg-green-50 dark:bg-green-950/30'
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-green-200 dark:border-green-800 p-4">
        <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
        <h3 className="font-semibold text-green-800 dark:text-green-200">Task Created</h3>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Goal */}
        <p className="text-sm text-foreground">{task.goal}</p>

        {/* Metadata */}
        <div className="space-y-2 text-xs text-muted-foreground">
          {/* Session ID */}
          <div className="flex items-center gap-2">
            <FileText className="h-3.5 w-3.5" />
            <span>Session: {task.sessionId}</span>
          </div>

          {/* Repository */}
          {task.repositoryName && (
            <div className="flex items-center gap-2">
              <GitBranch className="h-3.5 w-3.5" />
              <span>Repository: {task.repositoryName}</span>
            </div>
          )}

          {/* Project */}
          {task.projectId && (
            <div className="flex items-center gap-2">
              <Folder className="h-3.5 w-3.5" />
              <span>Project: {task.projectId}</span>
            </div>
          )}

          {/* Work Item */}
          {task.workItemId && (
            <div className="flex items-center gap-2">
              <FileText className="h-3.5 w-3.5" />
              <span>Work Item: #{task.workItemId}</span>
            </div>
          )}

          {/* Constraints */}
          {task.constraints && task.constraints.length > 0 && (
            <div className="flex items-start gap-2">
              <ListChecks className="h-3.5 w-3.5 mt-0.5" />
              <div>
                <span className="font-medium">Constraints:</span>
                <ul className="list-disc list-inside ml-1">
                  {task.constraints.map((constraint, index) => (
                    <li key={index}>{constraint}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Created time */}
          <div className="text-muted-foreground">
            Created: {formattedDate}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="border-t border-green-200 dark:border-green-800 p-4">
        <Link
          to={`/orchestrate/${task.sessionId}`}
          className={cn(
            'inline-flex items-center gap-2 px-4 py-2 rounded-md',
            'bg-green-600 hover:bg-green-700 text-white',
            'text-sm font-medium transition-colors'
          )}
        >
          <ExternalLink className="h-4 w-4" />
          Open Task
        </Link>
      </div>
    </article>
  )
}
